import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService, graphBuilderApi } from '../../services/api';
import { EvidenceTimeline } from '../explainability';
import './ExplainabilityDrawer.css';
import '../UserHome/ExplainabilityPage.css';

const PREVIEW_LEN = 300;

function TruncatedText({ text, limit = PREVIEW_LEN }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const needsTruncation = text.length > limit;
  const display = !needsTruncation || expanded
    ? text
    : text.slice(0, limit).replace(/\s+\S*$/, '') + '...';
  return (
    <span className="xd-truncated-text">
      {display}
      {needsTruncation && (
        <button className="xd-show-more" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'show less' : 'show more'}
        </button>
      )}
    </span>
  );
}

function PillarSection({ title, icon, badge, narration, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`xd-pillar ${open ? 'xd-pillar-open' : ''}`}>
      <button
        className="xd-pillar-header"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="xd-pillar-icon">{icon}</span>
        <span className="xd-pillar-title">{title}</span>
        {badge != null && <span className="xd-pillar-badge">{badge}</span>}
        <span className="xd-pillar-chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="xd-pillar-body">
          {narration && (
            <div className="xd-narration">
              {narration}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ passed, label }) {
  return (
    <span className={`xd-status-badge ${passed ? 'xd-status-pass' : 'xd-status-fail'}`}>
      {label || (passed ? 'Satisfied' : 'Not satisfied')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pillar 1 — Answer
// ---------------------------------------------------------------------------
function AnswerPanel({ data }) {
  if (!data) return null;
  const bd = data.confidence_breakdown;
  return (
    <div className="xd-answer-panel">
      <div className="xd-answer-text">
        <TruncatedText text={data.answer} limit={600} />
      </div>
      {data.intent && (
        <div className="xd-answer-meta">
          <span className="xd-meta-label">Intent:</span> {data.intent}
        </div>
      )}
      {data.decision_label && (
        <div className="xd-answer-meta">
          <span className="xd-meta-label">Decision node:</span> {data.decision_label}
        </div>
      )}
      {data.confidence_pct != null && (
        <div className={`xd-confidence-bar xd-confidence-${data.confidence_level}`}>
          <div className="xd-confidence-fill" style={{ width: `${Math.min(data.confidence_pct, 100)}%` }} />
          <span className="xd-confidence-label">
            Confidence: {data.confidence_pct}% ({data.confidence_level})
          </span>
        </div>
      )}
      {bd && bd.version === 'v3_1_citation' && (
        <div className="xd-breakdown">
          <div className="xd-breakdown-title">
            Confidence breakdown
            <span className="xd-breakdown-version"> ({bd.version})</span>
          </div>
          {(() => {
            const V3_1_FACTOR_LABELS = {
              cited_faithfulness: 'Citation faithfulness',
              no_hidden_salient: 'No hidden evidence',
              prompt_useful: 'Prompt was useful',
            };
            const V3_1_FACTOR_TOOLTIPS = {
              cited_faithfulness:
                'Of the prompt nodes the LLM cited, how well their content '
                + 'survived in the answer. For text nodes the matcher looks '
                + 'at term overlap; for image nodes it checks that the '
                + '/api/assets/<uuid>/download URL appears in the answer.',
              no_hidden_salient:
                'Of the top SNN-ranked content nodes (excluding scope, '
                + 'gates, and convenience supplements), how many the LLM '
                + 'either cited or visibly rendered.',
              prompt_useful:
                'Did the prompt actually carry usable evidence and did the '
                + 'LLM cite at least one node from it?',
            };
            const factors = bd.factors || {};
            const rows = [];
            ['cited_faithfulness', 'no_hidden_salient', 'prompt_useful'].forEach((key) => {
              const f = factors[key];
              if (!f || typeof f !== 'object') return;
              const value = f.value ?? 0;
              const weight = f.weight ?? 0;
              const contribution = f.contribution ?? 0;
              rows.push(
                <div key={key} className="xd-breakdown-row">
                  <span
                    className="xd-breakdown-name"
                    title={V3_1_FACTOR_TOOLTIPS[key]}
                  >
                    {V3_1_FACTOR_LABELS[key]}
                  </span>
                  <span className="xd-breakdown-value">
                    {(value * 100).toFixed(1)}%
                    {weight > 0 && (
                      <>
                        {' '}(wt {(weight * 100).toFixed(0)}%, +{(contribution * 100).toFixed(1)}%)
                      </>
                    )}
                    {f.detail ? <> {' · '}<span className="xd-breakdown-detail">{f.detail}</span></> : null}
                  </span>
                </div>
              );
            });
            if (bd.applied_floor && typeof bd.applied_floor === 'object') {
              const floorPct = ((bd.applied_floor.value ?? 0) * 100).toFixed(0);
              const reason = (bd.applied_floor.reason || '').replace(/_/g, ' ');
              rows.push(
                <div key="applied_floor" className="xd-breakdown-row">
                  <span className="xd-breakdown-name">Floor applied</span>
                  <span className="xd-breakdown-value">
                    lifted to {floorPct}% ({reason})
                  </span>
                </div>
              );
            }
            if (bd.applied_cap && typeof bd.applied_cap === 'object') {
              const capPct = ((bd.applied_cap.value ?? 0) * 100).toFixed(0);
              const reason = (bd.applied_cap.reason || '').replace(/_/g, ' ');
              rows.push(
                <div key="applied_cap" className="xd-breakdown-row">
                  <span className="xd-breakdown-name">Cap applied</span>
                  <span className="xd-breakdown-value">
                    held at {capPct}% ({reason})
                  </span>
                </div>
              );
            }
            return rows;
          })()}
        </div>
      )}
      {bd && bd.version === 'v3_simple' && (
        <div className="xd-breakdown">
          <div className="xd-breakdown-title">
            Confidence breakdown
            <span className="xd-breakdown-version"> ({bd.version})</span>
          </div>
          {(() => {
            // Friendly labels for the three v3_simple factors.
            const V3_FACTOR_LABELS = {
              prompt_has_content: 'Prompt has content',
              top_k_coverage: 'Top-evidence coverage',
              groundedness: 'Grounded in prompt',
            };
            const factors = bd.factors || {};
            const rows = [];
            ['prompt_has_content', 'top_k_coverage', 'groundedness'].forEach((key) => {
              const f = factors[key];
              if (!f || typeof f !== 'object') return;
              const value = f.value ?? 0;
              const weight = f.weight ?? 0;
              const contribution = f.contribution ?? 0;
              rows.push(
                <div key={key} className="xd-breakdown-row">
                  <span className="xd-breakdown-name">{V3_FACTOR_LABELS[key]}</span>
                  <span className="xd-breakdown-value">
                    {(value * 100).toFixed(1)}%
                    {weight > 0 && (
                      <>
                        {' '}(wt {(weight * 100).toFixed(0)}%, +{(contribution * 100).toFixed(1)}%)
                      </>
                    )}
                    {f.detail ? <> {' · '}<span className="xd-breakdown-detail">{f.detail}</span></> : null}
                  </span>
                </div>
              );
            });
            if (bd.applied_floor && typeof bd.applied_floor === 'object') {
              const floorPct = ((bd.applied_floor.value ?? 0) * 100).toFixed(0);
              const reason = (bd.applied_floor.reason || '').replace(/_/g, ' ');
              rows.push(
                <div key="applied_floor" className="xd-breakdown-row">
                  <span className="xd-breakdown-name">Floor applied</span>
                  <span className="xd-breakdown-value">
                    lifted to {floorPct}% ({reason})
                  </span>
                </div>
              );
            }
            if (bd.applied_cap && typeof bd.applied_cap === 'object') {
              const capPct = ((bd.applied_cap.value ?? 0) * 100).toFixed(0);
              const reason = (bd.applied_cap.reason || '').replace(/_/g, ' ');
              rows.push(
                <div key="applied_cap" className="xd-breakdown-row">
                  <span className="xd-breakdown-name">Cap applied</span>
                  <span className="xd-breakdown-value">
                    held at {capPct}% ({reason})
                  </span>
                </div>
              );
            }
            return rows;
          })()}
        </div>
      )}
      {bd && bd.version !== 'v3_simple' && bd.version !== 'v3_1_citation' && (
        <div className="xd-breakdown">
          <div className="xd-breakdown-title">
            Confidence breakdown
            {bd.version && (
              <span className="xd-breakdown-version"> ({bd.version})</span>
            )}
          </div>
          {Object.entries(bd).map(([key, val]) => {
            // Skip top-level scalars; we only render component objects.
            if (key === 'version' || key === 'base_score' || key === 'after_bonus') return null;
            if (val == null || typeof val !== 'object') return null;

            // Grounding floor (v2_potency, replaces strength_floor): protects
            // accurate, well-cited answers (verbatim or paraphrased) where
            // the LLM grounded each sentence in a strong pool node.
            if (key === 'grounding_floor') {
              if (!val.applied) return null;
              const density = (val.grounding_density ?? 0) * 100;
              const grounded = val.grounded_cited_count ?? 0;
              const eff = val.effective_cite_count ?? 0;
              return (
                <div key={key} className="xd-breakdown-row">
                  <span className="xd-breakdown-name">grounding floor</span>
                  <span className="xd-breakdown-value">
                    lifted to {((val.floor_value ?? 0) * 100).toFixed(1)}%
                    {' '}(density {density.toFixed(0)}%, {grounded}/{eff} cites grounded)
                  </span>
                </div>
              );
            }

            // Legacy strength_floor block -- only render for older insights
            // that pre-date the grounding_floor rollout.  The new scorer
            // always emits applied=false here, so it stays hidden.
            if (key === 'strength_floor') {
              if (!val.applied) return null;
              if (val.deprecated) return null;
              if (bd.grounding_floor) return null;
              return (
                <div key={key} className="xd-breakdown-row">
                  <span className="xd-breakdown-name">strength floor</span>
                  <span className="xd-breakdown-value">
                    lifted to {((val.floor_value ?? 0) * 100).toFixed(1)}%
                    {' '}(strength {((val.strength ?? 0) * 100).toFixed(1)}%)
                  </span>
                </div>
              );
            }

            // Citation bonus is additive (not weighted in the base sum).
            if (key === 'citation_bonus') {
              const contrib = val.contribution ?? 0;
              if (!contrib && !val.value) return null;
              const boostedTag = val.weight_boosted ? ' · boosted' : '';
              return (
                <div key={key} className="xd-breakdown-row">
                  <span className="xd-breakdown-name">citation bonus</span>
                  <span className="xd-breakdown-value">
                    {((val.value ?? 0) * 100).toFixed(1)}%
                    {' '}(+{(contrib * 100).toFixed(1)}%, {val.citation_count ?? 0}/
                    {val.expected_citations ?? '?'} cites{boostedTag})
                  </span>
                </div>
              );
            }

            const displayVal = val.rescaled_value ?? val.relative_value ?? val.value;
            const weight = val.weight ?? 0;
            const contribution = val.contribution ?? 0;

            // Optional inline annotation for the v2_potency-specific rows.
            let annotation = null;
            if (key === 'evidence_mass' && val.pool_size != null) {
              annotation = ` · pool ${val.pool_size}`;
            } else if (key === 'top_node_strength' && val.top_render_match_type) {
              annotation = ` · ${val.top_render_match_type.replace(/_/g, ' ')}`;
            }

            return (
              <div key={key} className="xd-breakdown-row">
                <span className="xd-breakdown-name">{key.replace(/_/g, ' ')}</span>
                <span className="xd-breakdown-value">
                  {displayVal != null ? `${(displayVal * 100).toFixed(1)}%` : '—'}
                  {weight > 0 && (
                    <>
                      {' '}(wt {(weight * 100).toFixed(0)}%, +{(contribution * 100).toFixed(1)}%)
                    </>
                  )}
                  {annotation}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pillar 2 — Rules
// ---------------------------------------------------------------------------
function RulesPanel({ data }) {
  if (!data) return null;
  const rules = data.rules_evaluated || [];
  if (rules.length === 0) {
    return <p className="xd-empty-notice">{data.summary || 'No rules were evaluated.'}</p>;
  }
  return (
    <div className="xd-rules-panel">
      <p className="xd-rules-summary">{data.summary}</p>
      {rules.map((rule, idx) => (
        <div key={rule.gate_id || idx} className={`xd-rule-card ${rule.satisfied ? 'xd-rule-satisfied' : 'xd-rule-unsatisfied'}`}>
          <div className="xd-rule-header">
            <span className="xd-rule-type">{rule.gate_type}</span>
            <span className="xd-rule-label">{rule.label}</span>
            <StatusBadge passed={rule.satisfied} />
          </div>
          {rule.rule_text && (
            <div className="xd-rule-text">
              <TruncatedText text={rule.rule_text} limit={400} />
            </div>
          )}
          <div className="xd-rule-progress">
            Inputs: {rule.satisfaction_progress}
            {rule.source_doc_name && (
              <span className="xd-rule-source"> | Source: {rule.source_doc_name}</span>
            )}
          </div>
          {rule.fired_inputs && rule.fired_inputs.length > 0 && (
            <div className="xd-rule-inputs">
              <span className="xd-meta-label">Selected:</span>{' '}
              {rule.fired_inputs.join(', ')}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pillar 3 — Sources
// ---------------------------------------------------------------------------
function SourcesPanel({ data }) {
  if (!data) return null;
  const grouped = data.grouped_by_document || {};
  const docs = Object.keys(grouped);
  const dropped = data.sources_dropped || [];

  if (data.total_used === 0 && dropped.length === 0) {
    return <p className="xd-empty-notice">No source documents were referenced.</p>;
  }

  const getDocGroup = (doc) => {
    const entry = grouped[doc];
    if (Array.isArray(entry)) return { items: entry, uploaded_by: '' };
    return { items: entry?.items || [], uploaded_by: entry?.uploaded_by || '' };
  };

  return (
    <div className="xd-sources-panel">
      <div className="xd-sources-summary">
        {data.total_used} source(s) used
        {data.total_dropped > 0 && <>, {data.total_dropped} dropped</>}
      </div>
      {docs.map(doc => {
        const { items, uploaded_by } = getDocGroup(doc);
        return (
          <div key={doc} className="xd-source-group">
            <div className="xd-source-doc-name">
              <span>{doc}</span>
              {uploaded_by && (
                <span className="xd-source-uploaded-by">Uploaded by: {uploaded_by}</span>
              )}
            </div>
            {items.map((src, idx) => (
              <div key={src.node_id || idx} className="xd-source-item">
                {src.label && <div className="xd-source-label">{src.label}</div>}
                {src.text && (
                  <div className="xd-source-text">
                    <TruncatedText text={src.text} limit={400} />
                  </div>
                )}
                <div className="xd-source-meta">
                  {src.source_doc_section && <span>Section: {src.source_doc_section}</span>}
                  {src.created_by && <span> | By: {src.created_by}</span>}
                  {src.create_date && <span> | {src.create_date}</span>}
                </div>
              </div>
            ))}
          </div>
        );
      })}
      {dropped.length > 0 && (
        <DroppedSources items={dropped} />
      )}
    </div>
  );
}

function DroppedSources({ items }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="xd-dropped-section">
      <button className="xd-dropped-toggle" onClick={() => setOpen(o => !o)}>
        {open ? 'Hide' : 'Show'} {items.length} dropped source(s)
      </button>
      {open && (
        <ul className="xd-dropped-list">
          {items.map((d, idx) => (
            <li key={d.node_id || idx} className="xd-dropped-item">
              <span className="xd-dropped-label">{d.label || d.node_id}</span>
              <span className="xd-dropped-reason">{d.reason}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pillar 4 — Validation
// ---------------------------------------------------------------------------
function ValidationPanel({ data }) {
  if (!data) return null;
  const checks = data.validation_checks || [];
  return (
    <div className="xd-validation-panel">
      <div className="xd-validation-summary">
        {data.passed_checks}/{data.total_checks} checks passed
      </div>
      {checks.map((check, idx) => (
        <div key={check.check || idx} className="xd-validation-check">
          <div className="xd-validation-check-header">
            <StatusBadge passed={check.passed} label={check.passed ? 'Pass' : 'Fail'} />
            <span className="xd-validation-check-name">{check.check.replace(/_/g, ' ')}</span>
          </div>
          <div className="xd-validation-check-desc">{check.description}</div>
          {check.detail && (
            <div className="xd-validation-detail">
              {Object.entries(check.detail).map(([k, v]) => (
                <div key={k} className="xd-detail-row">
                  <span className="xd-detail-key">{k.replace(/_/g, ' ')}</span>
                  <span className="xd-detail-value">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pillar 5 — Constraint Proof
// ---------------------------------------------------------------------------
function NodeThresholdProofRow({ p, onNodeClick }) {
  return (
    <div className="xd-proof-item xd-proof-item-compact">
      <StatusBadge passed={p.result} label={p.fired ? 'Selected' : 'Silent'} />
      <span className="xd-proof-node-label">
        {onNodeClick ? (
          <button
            type="button"
            className="trace-node-link"
            onClick={() => onNodeClick(p.node_id || p.id)}
          >
            {p.label}
          </button>
        ) : (
          p.label
        )}
      </span>
      {p.threshold != null && <span className="xd-proof-detail">threshold: {p.threshold}</span>}
      <span className="xd-proof-detail">selections: {p.spike_count}</span>
      <span className="xd-proof-detail">{p.mean_rate_hz}</span>
    </div>
  );
}

function ConstraintProofPanel({ data, onNodeClick }) {
  if (!data) return null;
  const summary = data.summary || {};
  const gateProofs = data.gate_proofs || [];
  const nodeProofs = data.node_proofs || [];
  const synapseProofs = data.synapse_proofs || [];

  const synapsePct = summary.synapses_total > 0
    ? ((summary.synapses_valid / summary.synapses_total) * 100).toFixed(1)
    : null;

  return (
    <div className="xd-proof-panel">
      {/* Overall status */}
      <div className={`xd-proof-overall ${summary.all_constraints_satisfied ? 'xd-proof-pass' : 'xd-proof-fail'}`}>
        {summary.all_constraints_satisfied
          ? 'All internal constraints were satisfied.'
          : 'Some constraints were not satisfied.'}
      </div>

      <div className="xd-proof-stats">
        <div className="xd-proof-stat">
          {summary.gates_total > 0 ? (
            <>
              <span className="xd-proof-stat-value">{summary.gates_satisfied}/{summary.gates_total}</span>
              <span className="xd-proof-stat-label">Gates satisfied</span>
            </>
          ) : (
            <>
              <span className="xd-proof-stat-value">—</span>
              <span className="xd-proof-stat-label">No gates in graph</span>
            </>
          )}
        </div>
        <div className="xd-proof-stat">
          <span className="xd-proof-stat-value">{summary.nodes_fired}/{summary.nodes_total}</span>
          <span className="xd-proof-stat-label">Nodes selected</span>
        </div>
        <div className="xd-proof-stat">
          <span className="xd-proof-stat-value">{summary.synapses_valid}/{summary.synapses_total}</span>
          <span className="xd-proof-stat-label">
            Connections valid{synapsePct != null && synapsePct !== '100.0' ? ` (${synapsePct}%)` : ''}
          </span>
        </div>
      </div>

      {/* Gate proofs */}
      {gateProofs.length > 0 && (
        <ProofSubsection title="Gate Constraints" items={gateProofs} renderItem={(p, idx) => (
          <div key={p.id || idx} className="xd-proof-item">
            <div className="xd-proof-item-header">
              <StatusBadge passed={p.result} />
              <span className="xd-proof-gate-type">{p.gate_type}</span>
            </div>
            <div className="xd-proof-assertion">{p.assertion}</div>
            <div className="xd-proof-progress">Progress: {p.progress} ({p.received_count}/{p.required_count})</div>
          </div>
        )} />
      )}

      {/* Node threshold proofs (show top 20 for brevity) */}
      {nodeProofs.length > 0 && (
        <ProofSubsection
          title="Node Thresholds"
          items={nodeProofs.slice(0, 20)}
          renderItem={(p, idx) => (
            <NodeThresholdProofRow key={p.id || idx} p={p} onNodeClick={onNodeClick} />
          )}
          extraInfo={nodeProofs.length > 20 ? `Showing 20 of ${nodeProofs.length} nodes` : null}
        />
      )}

      {/* Synapse weight proofs (show top 20) */}
      {synapseProofs.length > 0 && (
        <ProofSubsection title="Connection Mass" items={synapseProofs.slice(0, 20)} renderItem={(p, idx) => (
          <div key={p.id || idx} className="xd-proof-item xd-proof-item-compact">
            <StatusBadge passed={p.result} label={p.result ? 'Valid' : 'Out of bounds'} />
            <span className="xd-proof-synapse-info">
              {p.source_label} &rarr; {p.target_label}
            </span>
            <span className="xd-proof-detail">w={p.raw_weight}</span>
            {p.modulated_weight != null && <span className="xd-proof-detail">eff={p.modulated_weight}</span>}
            {p.relevance != null && <span className="xd-proof-detail">rel={p.relevance}</span>}
          </div>
        )} extraInfo={synapseProofs.length > 20 ? `Showing 20 of ${synapseProofs.length} connections` : null} />
      )}
    </div>
  );
}

function ProofSubsection({ title, items, renderItem, extraInfo }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="xd-proof-subsection">
      <button className="xd-proof-subsection-header" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>{title}</span>
        <span className="xd-pillar-badge">{items.length}</span>
        <span className="xd-pillar-chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="xd-proof-subsection-body">
          {items.map(renderItem)}
          {extraInfo && <div className="xd-proof-extra">{extraInfo}</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pillar 6 — Gated Decision Tree (on-demand from /pipeline/explain)
// ---------------------------------------------------------------------------
function DecisionTreePanel({ queryRunId, settings }) {
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    if (!queryRunId || loading) return;
    setLoading(true);
    setError(null);
    try {
      let providerId = undefined;
      let llmModel = undefined;
      if (settings?.llm_model && settings.llm_model.startsWith('provider::')) {
        const parts = settings.llm_model.split('::');
        providerId = parts[1];
        llmModel = parts[2];
      }
      const result = await graphBuilderApi.getGatedDecisionTree(queryRunId, {
        provider_id: providerId,
        llm_model: llmModel || 'gpt-4o',
        include_alternatives: true,
      });
      setTreeData(result);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to generate decision tree.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  if (!queryRunId) {
    return (
      <p className="xd-empty-notice">
        Decision tree is not available for this response (no query run ID).
      </p>
    );
  }

  if (!treeData && !loading && !error) {
    return (
      <div className="xd-dt-prompt">
        <p className="xd-dt-description">
          Generate an on-demand regulatory decision tree that explains the reasoning
          behind this answer using gated branching logic, alternative scenarios, and
          source document references.
        </p>
        <button className="xd-dt-generate-btn" onClick={handleGenerate}>
          Generate Decision Tree
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="xd-loading">
        <div className="xd-spinner" />
        <span>Generating decision tree...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="xd-dt-error">
        <div className="xd-error-notice">{error}</div>
        <button className="xd-dt-generate-btn xd-dt-retry" onClick={handleGenerate}>
          Retry
        </button>
      </div>
    );
  }

  const tree = treeData.gated_decision_tree || '';
  const alternatives = treeData.alternative_scenarios || [];
  const gatesSummary = treeData.gates_summary || [];
  const factors = treeData.contributing_factors || [];
  const docs = treeData.source_documents || [];

  return (
    <div className="xd-dt-panel">
      <div className="xd-dt-tree-text">{tree}</div>

      {gatesSummary.length > 0 && (
        <div className="xd-dt-section">
          <div className="xd-dt-section-title">Gates ({gatesSummary.length})</div>
          {gatesSummary.map((g, i) => (
            <div key={i} className={`xd-dt-gate ${g.is_satisfied ? 'xd-dt-gate-satisfied' : 'xd-dt-gate-unsatisfied'}`}>
              <div className="xd-dt-gate-header">
                <StatusBadge passed={g.is_satisfied} />
                <span className="xd-dt-gate-label">{g.gate_label}</span>
              </div>
              <div className="xd-dt-gate-trigger">{g.trigger}</div>
              <div className="xd-dt-gate-detail">
                <span>Concept: {g.concept_area || 'N/A'}</span>
                {g.missing_inputs_labels?.length > 0 && (
                  <span className="xd-dt-gate-missing">
                    Missing: {g.missing_inputs_labels.join(', ')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {factors.length > 0 && (
        <div className="xd-dt-section">
          <div className="xd-dt-section-title">Key Factors ({factors.length})</div>
          {factors.slice(0, 10).map((f, i) => (
            <div key={i} className="xd-dt-factor">
              <span className={`xd-dt-relevance xd-dt-relevance-${f.relevance}`}>{f.relevance}</span>
              <span className="xd-dt-factor-name">{f.factor}</span>
              <span className="xd-dt-factor-role">{f.role}</span>
            </div>
          ))}
        </div>
      )}

      {alternatives.length > 0 && (
        <div className="xd-dt-section">
          <div className="xd-dt-section-title">Alternative Scenarios</div>
          <ul className="xd-dt-alternatives">
            {alternatives.map((alt, i) => (
              <li key={i}>{alt}</li>
            ))}
          </ul>
        </div>
      )}

      {docs.length > 0 && (
        <div className="xd-dt-section">
          <div className="xd-dt-section-title">Source Documents ({docs.length})</div>
          <ul className="xd-dt-docs">
            {docs.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Drawer
// ---------------------------------------------------------------------------
function ExplainabilityDrawer({ message, onClose, onViewFullTrace, settings, onNodeClick }) {
  const drawerRef = useRef(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [narrations, setNarrations] = useState(null);
  const [narratingLoading, setNarratingLoading] = useState(false);

  const [bizExplanation, setBizExplanation] = useState(null);
  const [bizLoading, setBizLoading] = useState(false);
  const [bizError, setBizError] = useState(null);
  const [showTechnical, setShowTechnical] = useState(false);

  const insightId = message?.metadata?.insightId;
  const queryRunId = message?.metadata?.query_run_id;
  const isConversational = message?.metadata?.routing === 'CONVERSATIONAL';
  const hasExplainTarget = !!(insightId || queryRunId);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (drawerRef.current) {
      const focusable = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length) focusable[0].focus();
    }
  }, []);

  // Primary: fetch business explanation when drawer opens
  useEffect(() => {
    if (!hasExplainTarget || isConversational) return;
    let cancelled = false;
    setBizLoading(true);
    setBizError(null);

    graphBuilderApi.getBusinessExplanation({
      insight_id: insightId || undefined,
      query_run_id: queryRunId || undefined,
      domain: 'general',
      include_narrative: true,
    })
      .then(data => {
        if (cancelled) return;
        setBizExplanation(data);
      })
      .catch(err => {
        if (cancelled) return;
        setBizError(err.message || 'Failed to load business explanation.');
      })
      .finally(() => {
        if (!cancelled) setBizLoading(false);
      });

    return () => { cancelled = true; };
  }, [insightId, queryRunId, hasExplainTarget, isConversational]);

  // Lazy: fetch five-pillar report only when Technical Details is expanded
  useEffect(() => {
    if (!showTechnical || !insightId || isConversational || report) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    apiService.getExplainabilityReport(insightId)
      .then(res => {
        if (cancelled) return;
        if (res.success && res.report) {
          setReport(res.report);
        } else {
          setError(res.error || 'Failed to load explanation.');
        }
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message || 'Failed to load explanation.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [showTechnical, insightId, isConversational, report]);

  const handleNarrate = async () => {
    if (!insightId || narratingLoading) return;
    setNarratingLoading(true);
    try {
      const res = await apiService.getNarration(insightId);
      if (res.success && res.narrations) {
        setNarrations(res.narrations);
      }
    } catch (err) {
      console.error('Narration failed:', err);
    } finally {
      setNarratingLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    if (!insightId) return;
    try {
      const res = await apiService.getConstraintCertificate(insightId);
      if (res.success && res.certificate) {
        const json = JSON.stringify(res.certificate, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificate-${insightId.slice(0, 8)}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Certificate download failed:', err);
    }
  };

  if (!message) return null;

  const pillars = report?.pillars;

  return (
    <div className="xd-overlay" onClick={onClose} role="presentation">
      <div
        className="xd-drawer"
        ref={drawerRef}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-label="Explainability Report"
      >
        {/* Header */}
        <div className="xd-drawer-header">
          <h3>Why did I answer this way?</h3>
          <button className="xd-close-btn" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {/* Scrollable body */}
        <div className="xd-drawer-body">
          {isConversational ? (
            <div className="xd-conversational-notice">
              This was a conversational response. The system answered your
              question directly without looking up additional information,
              so there is no information trail to show.
            </div>
          ) : bizLoading ? (
            <div className="xd-loading">
              <div className="xd-spinner" />
              <span>Loading explanation...</span>
            </div>
          ) : bizError && !bizExplanation ? (
            <div className="xd-error-notice">{bizError}</div>
          ) : bizExplanation ? (
            <>
              {bizExplanation.narrative?.executive_summary && (
                <div className="xd-business-summary">
                  <h4>Executive Summary</h4>
                  <p>{bizExplanation.narrative.executive_summary}</p>
                </div>
              )}

              <div className="xd-business-decision">
                <div className="xd-business-decision-item">
                  <span className="xd-business-decision-label">Decision</span>
                  <span className="xd-business-decision-value">{bizExplanation.decision || '-'}</span>
                </div>
                <div className="xd-business-decision-item">
                  <span className="xd-business-decision-label">Confidence</span>
                  <span className="xd-business-decision-value">{bizExplanation.confidence || '-'}</span>
                </div>
                <div className="xd-business-decision-item">
                  <span className="xd-business-decision-label">Evidence</span>
                  <span className="xd-business-decision-value">{bizExplanation.evidence_factors?.length || 0} factors</span>
                </div>
              </div>

              <EvidenceTimeline
                timeline={bizExplanation.evaluation_timeline || []}
                evidenceFactors={bizExplanation.evidence_factors || []}
                excludedItems={bizExplanation.excluded_information || []}
                decisionRules={bizExplanation.decision_rules_evaluated || []}
              />

              {insightId && (
                <button
                  className="xd-business-full-link"
                  onClick={() => {
                    onClose();
                    const path = window.location.pathname;
                    const match = path.match(/\/graphs\/build\/([^/]+)/);
                    if (match) {
                      window.location.href = `/user-home/graphs/build/${match[1]}/build-tune/explainability?insight=${insightId}`;
                    }
                  }}
                >
                  View Full Analysis
                </button>
              )}

              {/* Collapsible Five-Pillar Technical Report */}
              {insightId && (
                <PillarSection
                  title="Five-Pillar Technical Report"
                  icon="T"
                  defaultOpen={false}
                >
                  <div
                    style={{ cursor: showTechnical ? 'default' : 'pointer' }}
                    onClick={() => { if (!showTechnical) setShowTechnical(true); }}
                  >
                    {!showTechnical ? (
                      <div className="xd-empty-notice">Click to load detailed technical report...</div>
                    ) : loading ? (
                      <div className="xd-loading">
                        <div className="xd-spinner" />
                        <span>Loading technical report...</span>
                      </div>
                    ) : error ? (
                      <div className="xd-error-notice">{error}</div>
                    ) : pillars ? (
                      <>
                        {!narrations && (
                          <button
                            className="xd-narrate-btn"
                            onClick={handleNarrate}
                            disabled={narratingLoading}
                          >
                            {narratingLoading ? 'Summarizing...' : 'Summarize in Plain English'}
                          </button>
                        )}

                        <PillarSection title="The Answer" icon="A" narration={narrations?.answer} defaultOpen={false}>
                          <AnswerPanel data={pillars.answer} />
                        </PillarSection>
                        <PillarSection title="The Rules" icon="R" badge={pillars.rules?.total_gates} narration={narrations?.rules} defaultOpen={false}>
                          <RulesPanel data={pillars.rules} />
                        </PillarSection>
                        <PillarSection title="The Sources" icon="S" badge={pillars.sources?.total_used} narration={narrations?.sources} defaultOpen={false}>
                          <SourcesPanel data={pillars.sources} />
                        </PillarSection>
                        <PillarSection title="Validation Logic" icon="V" badge={`${pillars.validation?.passed_checks}/${pillars.validation?.total_checks}`} narration={narrations?.validation}>
                          <ValidationPanel data={pillars.validation} />
                        </PillarSection>
                        <PillarSection title="Constraint Proof" icon="P" badge={pillars.constraint_proof?.summary?.all_constraints_satisfied ? 'Pass' : 'Fail'} narration={narrations?.constraint_proof}>
                          <ConstraintProofPanel data={pillars.constraint_proof} onNodeClick={onNodeClick} />
                        </PillarSection>
                      </>
                    ) : (
                      <div className="xd-empty-notice">No technical report available.</div>
                    )}
                  </div>
                </PillarSection>
              )}

              {/* Decision Tree (on-demand via /pipeline/explain) */}
              {message?.metadata?.query_run_id && (
                <PillarSection title="Decision Tree" icon="D" narration={null} defaultOpen={false}>
                  <DecisionTreePanel queryRunId={message.metadata.query_run_id} settings={settings} />
                </PillarSection>
              )}
            </>
          ) : (
            <div className="xd-empty-notice">
              No explanation data is available for this response.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="xd-drawer-footer">
          {insightId && onViewFullTrace && (
            <button
              className="xd-footer-btn xd-btn-trace"
              onClick={() => { onViewFullTrace(insightId); onClose(); }}
            >
              View Full Trace
            </button>
          )}
          {insightId && !isConversational && (
            <button className="xd-footer-btn xd-btn-download" onClick={handleDownloadCertificate}>
              Download Certificate
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExplainabilityDrawer;
